/**
 * Fachbeiträge für /impulse
 * body: Absätze durch \n\n trennen; Zwischenüberschriften mit ### am Zeilenanfang
 */
const impulses = [
  {
    id: 3,
    slug: 'das-bild-das-andere-von-ihnen-haben',
    title: 'Das Bild, das andere von Ihnen haben',
    date: '2026-10-16',
    locationLine: 'München, im Oktober 2026 · Michaela Braun',
    body: `Irgendwo trägt jemand ein Urteil über Sie mit sich herum. Es ist fertig, es fühlt sich für ihn richtig an … und es trifft Sie nicht.

Vielleicht hatte die Person einen schlechten Tag. Vielleicht fehlte ein Teil der Geschichte. Vielleicht wurde Ihre Ruhe als Distanz gelesen und Ihre Offenheit als Leichtigkeit.

Der Impuls, das zu korrigieren, ist verständlich. Er kann aber ein ganzes Leben in Anspruch nehmen.

Epiktet hat bereits vor fast zweitausend Jahren – also lange vor den „Circles of Influence“ – eine Grenzlinie gezogen, die bis heute gilt. Auf der einen Seite steht, was in Ihrer Macht liegt: Ihr Urteil, Ihre Haltung, Ihr Handeln. Auf der anderen Seite steht, was nicht in Ihrer Macht liegt. Das, was im Kopf anderer entsteht, denn dieser Kopf arbeitet mit seinen eigenen, subjektiven Eindrücken.

Aus dieser Grenzlinie folgt keine Gleichgültigkeit, sondern das richtige Maß.

Wenn ein konkretes Missverständnis besteht, wenn es bei jemandem entstanden ist, dessen Entscheidung für Sie zählt, und wenn Sie es mit Tatsachen ausräumen können, dann sprechen Sie es an. Einmal, mit Klarheit und ohne Erklärungsschleife.

Wenn nicht, werden Sie erkennen, dass das nicht in Ihrem Verantwortungsbereich liegt. Dadurch wird Kraft frei für das, was wirklich in Ihrem Wirkungskreis liegt: das gegebene Wort, der eingestandene Fehler, der anständige Umgang mit dem Menschen, der Ihnen gerade gegenübersitzt.

Mit der Zeit wird Ihr Handeln selbst zum belastbaren Beleg.

Manche werden Sie dennoch anders sehen, als Sie sind. Auch Sie sehen andere nicht immer, wie sie sind. In dieser Erkenntnis liegt für mich ein entlastendes Lächeln.

Das Bild, das andere von Ihnen haben, ist ein Spiegel. Die Frage ist nur, wessen Gesicht darin erscheint.

In meinen Reflexions-Räumen geht es genau darum: zu erkennen, welche Bilder Sie tragen, welche Ihnen zugeschrieben werden, und welche davon Ihre Aufmerksamkeit wirklich verdienen.`,
  },
  {
    id: 1,
    slug: 'frauen-karriere-macht',
    kicker: 'Frauen. Karriere. Macht.',
    title:
      'Das Politik-Hindernis: Warum Machtspiele Frauen ausbremsen – und was Unternehmen dagegen tun können',
    date: '2026-06-01',
    attribution:
      'Basierend auf: Salwender, Schoel, Bless & Stahlberg (2023) · Universität Mannheim',
    locationLine: 'München, im Juni 2026 · Michaela Braun',
    body: `Eine Studie der Universität Mannheim zeigt: In Unternehmenskulturen, die politisches Taktieren belohnen, bleiben Frauen deutlich häufiger außen vor und fühlen sich fehl am Platz. Das hat Folgen bis in die Chefetage.

Stellen Sie sich vor, Sie beginnen einen neuen Job. Schnell wird klar: Wer aufsteigt, tut das nicht unbedingt durch Können, sondern durch Vitamin B, strategisches Schweigen und ein Nicken zur richtigen Zeit.

Viele Frauen fühlen sich damit nicht besonders wohl. Das ist keine Frage fehlender Ambition. Es ist eine Frage der Passung. Ein Mannheimer Forschungsteam hat mit einer Studie belegt, dass diese Passung systematisch ungleich verteilt ist.

Das Konzept der „politischen Spielchen“, im Fachenglisch „organizational politics“ genannt, bezeichnet Verhaltensweisen, die auf Machtaufbau und Einfluss abzielen, oft jenseits formaler Strukturen. Seilschaften bilden. Informationen strategisch zurückhalten. Den richtigen Mentor wählen.

Die Studie legt nahe: Unternehmensstrukturen können den Karriereweg stark beeinflussen, unabhängig von individueller Leistung und Kompetenz.

Zusammengefasst nach Salwender et al., Social Psychological and Personality Science, 2023

### Was die Ergebnisse zeigen

Unter „Machtspiele“-Bedingungen haben Frauen deutlich mehr Angst vor negativen Reaktionen durch Kollegen und männliche Vorgesetzte. Außerdem zweifeln Frauen unter diesen Bedingungen deutlich häufiger an ihren eigenen Kompetenzen und Fähigkeiten. In der Folge kommt es zu einem sogenannten „Lack-of-Fit“-Erleben.

Es geht also nicht darum, dass Frauen grundsätzlich weniger Führungsambitionen hätten. Es geht darum, unter welchen Bedingungen sie diese Ambitionen entwickeln, oder eben nicht.

### Der tiefere Mechanismus

Hinter dem Begriff „Lack of Fit“ steckt eine mittlerweile gut belegte psychologische Theorie: Menschen erleben Stress und Selbstzweifel, wenn die Anforderungen einer Rolle nicht zu dem Bild passen, das sie von sich selbst haben. Wenn eine Unternehmenskultur Eigenschaften belohnt, die gesellschaftlich eher Männern zugeschrieben und auch häufiger von ihnen gezeigt werden, entsteht für viele Frauen eine stille Botschaft. Gemeint sind Durchsetzungsvermögen auf Kosten anderer, strategisches Taktieren und sichtbares Machtgebaren. Die Botschaft lautet: „Du gehörst nicht hierher.“

Wer glaubt, nicht die nötigen Fähigkeiten zu besitzen, strebt seltener nach Führungspositionen, unabhängig davon, ob dieser Zweifel berechtigt ist oder nicht.

### Was das für Unternehmen bedeutet

Wenn Unternehmen wollen, dass mehr Frauen Führungspositionen anstreben, müssen sich nicht die Frauen verändern. Die Unternehmen müssen ihre Kultur verändern.

Solange informelle Machtspiele karriereentscheidend sind, kann jede Diversitätsinitiative zwangsläufig nur an der Oberfläche kratzen. Mentoring-Programme, Quoten, Unconscious-Bias-Trainings: Sie alle greifen zu kurz, wenn die Grundstruktur des Aufstiegs intakt bleibt.

Drei Dinge können helfen. Erstens transparente Beförderungskriterien, die an tatsächliche Leistung gekoppelt sind. Zweitens eine Feedback-Kultur, die informelle Einflussnahme sichtbar und damit angreifbar macht. Drittens eine Führung, die politisches Taktieren weder belohnt noch still duldet.

### Was bleibt?

Die Frage, die bleibt, ist keine akademische. Jede Führungskraft, jede Personalabteilung und letztlich jeder Mitarbeitende muss sie für sich selbst beantworten: Was belohne ich hier wirklich?`,
  },
];

export default impulses;
